import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { withTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Col, Container, Form, Input, Modal, Row } from "reactstrap";
import { formatToEst } from "src/common/helper";
import { Loader } from "src/components";
import SunEditorReact from "src/components/sunEditorReact/SunEditorReact";
import { useUserContext } from "src/globals/providers";
import { TicketFieldSetting } from "src/sdk/models/Organisation";
import { Feedback, getTicketInitialValue, Ticket } from "src/sdk/models/Ticket";
import { TicketService } from "src/services/TicketService";
import { FeedbackColumns } from "./FeedbackColumns";

export interface TicketingDetailsProps {
  id?: number;
  ticketId?: string;
  modal_xlarge: boolean;
  setmodal_xlarge: (value: boolean) => void;
  tog_xlarge: () => void;
  fields: TicketFieldSetting[];
}

const service = new TicketService();
export const TicketingDetails: React.FC<TicketingDetailsProps> = ({
  id,
  ticketId,
  modal_xlarge,
  setmodal_xlarge,
  tog_xlarge,
  fields
}) => {
  const history = useHistory();
  const { user } = useUserContext();
  const [newFeedback, setNewFeedback] = useState("");
  const [ticket, setTicket] = useState<Ticket>(getTicketInitialValue());
  const [loading, setLoading] = useState<boolean>(true);
  const [newId, setNewId] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<"description" | "feedback" | "attachments">("description");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [editorKey, setEditorKey] = useState(0);

  const handleAddAttachments = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file: File = e.target.files[0];

    service.createFileByTicketId(ticket.id as number, file)
      .then(() => {
        toast.success("Created successfully");
        setNewFeedback("");
        service.getTicketById(newId)
          .then((res: any) => setTicket(res.data.item))
          .catch((err: any) => console.error(err))
          .finally(() => setLoading(false));
      })
      .catch((err: any) => {
        console.error(err);
        toast.error("Error while creating feedback");
      });
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (id) setNewId(id);
  }, [id]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: ticket,
    onSubmit: () => { },
  });

  useEffect(() => {
    if (!newId) return;
    setLoading(true);
    service.getTicketById(newId)
      .then((res: any) => setTicket(res.data.item))
      .catch((err: any) => console.error(err))
      .finally(() => setLoading(false));
  }, [newId]);

  useEffect(() => {
    if (!ticketId) return;
    setLoading(true);
    service.getTicketByTicketId(ticketId)
      .then((res: any) => setTicket(res.data.item))
      .catch((err: any) => console.error(err))
      .finally(() => setLoading(false));
  }, [ticketId]);

  const handleClose = () => {
    //////////////http://localhost:3000/ticketing?ticketId=TK003590
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete("ticketId");
    console.log()
    history.replace({
      pathname: "/ticketing",
      search: searchParams.toString(),
      state: history.location.state,
    });
    setmodal_xlarge(false);
  };

  const handleSaveFeedback = () => {
    const objNewFeedback: Feedback = {
      ticketId: ticket.id,
      createdBy: user?.username,
      feedback: newFeedback
    };

    service.createFeedback(objNewFeedback)
      .then(() => {
        toast.success("Feedback created successfully");
        setNewFeedback("");
        setEditorKey(prev => prev + 1);
        service.getTicketById(newId)
          .then((res: any) => setTicket(res.data.item))
          .catch((err: any) => console.error(err))
          .finally(() => setLoading(false));
      })
      .catch((err: any) => {
        console.error(err);
        toast.error("Error while creating feedback");
      });
  };

  const openAttachment = (file: any) => {
    const byteCharacters = atob(file.fileData);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: file.contentType });

    const url = URL.createObjectURL(blob);

    window.open(url, "_blank");

    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const showField = (fieldName: string) => fields.find(f => f.name === fieldName)?.showOnForm;

  return (
    <React.Fragment>
      <div className="container-fluid">
        <Modal
          size="xl"
          isOpen={modal_xlarge}
          toggle={handleClose}
          scrollable={true}
        >
          <div className="modal-header d-flex align-items-center justify-content-between">
            <h5 className="modal-title mt-0">Ticket Details - {ticket.ticketId}</h5>
            <div className="d-flex gap-2">
              <Button
                color="secondary"
                disabled={!ticket.previousId}
                onClick={() => {
                  if (ticket.previousId) {
                    setNewId(ticket.previousId);
                  }
                  setNewFeedback("");
                  setEditorKey(prev => prev + 1);
                }}
              >
                Previous
              </Button>
              <Button
                color="secondary"
                disabled={!ticket.nextId}
                onClick={() => {
                  if (ticket.nextId) setNewId(ticket.nextId);
                  setNewFeedback("");
                  setEditorKey(prev => prev + 1);
                }}
              >
                Next
              </Button>
            </div>
            <button
              onClick={handleClose}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#808080"}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#AAAAAA"}
              data-dismiss="modal"
              style={{
                backgroundColor: "#AAAAAA",
                color: "#FFFFFF",
                border: 0,
                borderRadius: 60,
                gap: 5,
                width: 100,
                paddingTop: 5,
                paddingBottom: 5,
                paddingLeft: 20,
                paddingRight: 20,
                display: "flex"
              }}
              type="button"
            >
              <i className={`bi-x-circle`} />
              Close
            </button>
          </div>
          <div className="modal-body">
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
                <Loader loading={true} />
              </div>
            ) : (
              <Form onSubmit={(e: any) => { e.preventDefault(); validation.handleSubmit(); return false; }}>
                <Container fluid className="p-3">

                  <Row className="mb-3">
                    {showField("facilityName") && (
                      <Col md={6}>
                        <label className="form-label"><i className="bi bi-building text-success me-1"></i> Facility</label>
                        <Input type="text" value={ticket.facilityName ?? "-"} readOnly disabled />
                      </Col>
                    )}
                    {showField("facilityGroupName") && (
                      <Col md={6}>
                        <label className="form-label"><i className="bi bi-building text-success me-1"></i> Facility Group</label>
                        <Input type="text" value={ticket.facilityGroupName ?? "-"} readOnly disabled />
                      </Col>
                    )}
                    {showField("status") && (
                      <Col md={6}>
                        <label className="form-label">Status</label>
                        <Input type="text" value={ticket.status ?? "-"} readOnly disabled />
                      </Col>
                    )}
                    {showField("priority") && (
                      <Col md={4}>
                        <label className="form-label">Priority</label>
                        <Input type="text" value={ticket.priority ?? "-"} readOnly disabled />
                      </Col>
                    )}
                  </Row>

                  <Row className="mb-3">
                    {showField("radiologist") && (
                      <Col md={4}>
                        <label className="form-label"><i className="bi bi-person text-success me-1"></i> Radiologist</label>
                        <Input type="text" value={ticket.radiologist ?? "-"} readOnly disabled />
                      </Col>
                    )}
                    {showField("category") && (
                      <Col md={4}>
                        <label className="form-label">Category</label>
                        <Input type="text" value={ticket.category ?? "-"} readOnly disabled />
                      </Col>
                    )}
                    {showField("subCategory") && (
                      <Col md={4}>
                        <label className="form-label">Sub-Category</label>
                        <Input type="text" value={ticket.subCategory ?? "-"} readOnly disabled />
                      </Col>
                    )}
                  </Row>

                  <Row className="mb-4">
                    {showField("contact") && (
                      <Col md={12}>
                        <label className="form-label"><i className="bi bi-telephone text-success me-1"></i> Contact</label>
                        <Input type="text" value={ticket.contact ?? "-"} readOnly disabled />
                      </Col>
                    )}
                    {showField("supportArea") && (
                      <Col md={6}>
                        <label className="form-label">Support Area</label>
                        <Input type="text" value={ticket.supportArea ?? "-"} readOnly disabled />
                      </Col>
                    )}
                  </Row>

                  <hr className="my-4" />

                  <Row className="mb-3">
                    {showField("createdBy") && (
                      <Col md={6}>
                        <label className="form-label">Ticket Created By</label>
                        <Input type="text" value={ticket.createdBy ?? "-"} readOnly disabled />
                      </Col>
                    )}
                    {showField("createdAt") && (
                      <Col md={6}>
                        <label className="form-label">Ticket Created At</label>
                        <Input
                          type="text"
                          value={formatToEst(ticket.createdAt)}
                          readOnly
                          disabled
                        />
                      </Col>
                    )}
                  </Row>

                  {showField("subject") && (
                    <Row className="mb-5">
                      <Col md={12}>
                        <label className="form-label">Subject</label>
                        <Input type="textarea" value={ticket.subject ?? "-"} readOnly disabled rows={6} />
                      </Col>
                    </Row>
                  )}

                  <hr style={{ marginTop: '80px', marginBottom: '16px' }} />

                  <div className="d-flex gap-4 mb-1 pb-2">
                    <label
                      className={`form-label mb-0 cursor-pointer ${activeTab === "description" ? "text-success" : "fw-bold"}`}
                      onClick={() => setActiveTab("description")}
                    >
                      Description
                    </label>
                    <label
                      className={`form-label mb-0 cursor-pointer ${activeTab === "feedback" ? "text-success" : "fw-bold"}`}
                      onClick={() => setActiveTab("feedback")}
                    >
                      Feedback ({ticket.feedbackItems?.length || 0})
                    </label>
                    <label
                      className={`form-label mb-0 cursor-pointer ${activeTab === "attachments" ? "text-success" : "fw-bold"}`}
                      onClick={() => setActiveTab("attachments")}
                    >
                      Attachments
                    </label>
                  </div>

                  {activeTab === "description" && (
                    <Row className="mb-3">
                      <Col md={12}>
                        <Input type="textarea" value={ticket.description ?? ""} readOnly disabled rows={6} />
                      </Col>
                    </Row>
                  )}

                  {activeTab === "feedback" && (
                    <>
                      <DataTable
                        columns={FeedbackColumns()}
                        data={ticket.feedbackItems || []}
                        noDataComponent="No feedback available."
                        striped
                        highlightOnHover
                        responsive
                        dense
                      />
                      <Row className="mb-3 mt-5">
                        <Col md={12}>
                          <div style={{ display: 'block' }}>
                            <SunEditorReact
                              key={editorKey}
                              name="feedback"
                              value={newFeedback}
                              onChange={(content) => setNewFeedback(content)}
                              placeholder="Add your feedback here..."
                            />
                          </div>
                        </Col>
                      </Row>
                      <div className="d-flex flex-row-reverse">
                        <div className="p-1">
                          <Button
                            color="primary"
                            style={{ backgroundColor: "#95CBB2", border: 0, color: "#FFFFFF" }}
                            onClick={handleSaveFeedback}
                          >
                            Submit Feedback
                          </Button>
                        </div>
                      </div>
                    </>
                  )}

                  {activeTab === "attachments" && (
                    <Row className="mb-3">
                      <Col md={12}>
                        <Input
                          type="file"
                          onChange={handleAddAttachments}
                        />

                        {/* Attachments list */}
                        <div className="mt-3">
                          {ticket.attachmentItems && ticket.attachmentItems.length === 0 && (
                            <p className="text-muted">No attachments added.</p>
                          )}

                          {ticket.attachmentItems && ticket.attachmentItems.map((file, idx) => (
                            <div
                              key={idx}
                              onClick={() => openAttachment(file)}
                              className="d-flex justify-content-between align-items-center p-2 mb-2"
                              style={{
                                border: "1px solid #ddd",
                                borderRadius: 6,
                                background: "#fafafa"
                              }}
                            >
                              <div>
                                <strong>{file.fileName}</strong>
                                <div className="text-muted" style={{ fontSize: 12 }}>
                                  {(file.fileSize / 1024).toFixed(1)} KB
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                      </Col>
                    </Row>
                  )}

                </Container>
              </Form>
            )}
          </div>
        </Modal>
      </div>
    </React.Fragment>
  );
}

export default withTranslation()(TicketingDetails);
